import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';

import { FormsModule } from '@angular/forms';
import { PaymentService } from '../../../services/payment.service';
import { AuthService } from '../../../services/auth.service';
import {
  PaymentBalanceResponse,
  ClientInvoices,
  Agreement,
  PaymentByProducerRequest,
  PaymentDetail,
  Invoice,
  AgreementQuotesRequest,
  AgreementQuotesResponse,
} from '../../../interfaces/payment.interfaces';

import { ModalFacturasComponent } from './helpers/modal-facturas.component';
import { Factura } from './helpers/modal-facturas.component';
import {
  Acuerdo,
  ModalAcuerdosComponent,
} from './helpers/modal-acuerdos.component';
import { NotificationComponent } from './helpers/notification.component';
import { GoogleAnalyticsService } from '../../../services/google-analytics/google-analytics.service';

interface ItemToDelete {
  tipo: 'factura' | 'acuerdo';
  data: any;
}

interface AcuerdoAgrupado {
  idAgreement: number;
  facturas: AgreementQuotesResponse[];
  totalPagar: number;
  tipo: string;
}

@Component({
  selector: 'app-pagos-online',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalFacturasComponent,
    ModalAcuerdosComponent,
    NotificationComponent,
  ],
  templateUrl: './pagos-online.component.html',
  styleUrls: ['./pagos-online.component.css'],
})
export class PagosOnlineComponent implements OnInit {
  insuranceData: PaymentBalanceResponse[] = [];
  title = 'Pagos Online';
  tipMessage = '';

  totalAPagar: number = 0;
  facturasSeleccionadas: any[] = [];
  acuerdosSeleccionados: any[] = [];

  facturasData: ClientInvoices | null = null;
  acuerdosData: Acuerdo[] = [];
  selectedClientCode: number | null = null;
  filterText = '';
  errorMessage = '';
  showFacturasModal = false;
  showAcuerdosModal = false;
  totalAmount = 0;
  mensajeNoData = 'No hay datos disponibles';
  errorStatus?: number;

  itemsPerPage: number = 5;
  currentPage: number = 1;
  totalPages: number = 0;
  paginatedData: any[] = [];

  facturasMapeadas: Factura[] = [];

  sortColumn: string | null = null;
  sortDesc: boolean = false;

  showModalPago = false;
  datosPago: PaymentByProducerRequest = {
    CardNumber: '',
    Expiration: '',
    CardCvv: '',
    BankCode: null,
    ProducerNumber: 0,
    Details: [],
  };

  itemToDelete: ItemToDelete | null = null;
  showConfirmModal = false;
  showConfirmacionPago = false;
  isProcessingPayment = false;

  showNotification = false;
  notificationType: 'success' | 'error' = 'success';
  notificationTitle = '';
  notificationMessage = '';

  visibleColumns: any[] = [];
  hiddenColumns: any[] = [];
  showColumnMenu: boolean = false;
  isSmallScreen: boolean = false;
  clientName: string = '';
  approvalNumber: string = '';

  clientesColumns = [
    { key: 'clientCode', label: 'Código' },
    { key: 'clientName', label: 'Nombre' },
    { key: 'rdBalance', label: 'Balance RD$' },
    { key: 'usBalance', label: 'Balance US$' },
    { key: 'agreementBalance', label: 'Balance de Acuerdo' },
    { key: 'actions', label: 'Acciones' },
  ];

  facturasColumns = [
    { key: 'poliza', label: 'Póliza', type: 'poliza' },
    { key: 'factura', label: 'Factura' },
    { key: 'currencyDescription', label: 'Moneda' },
    { key: 'currencyBalance', label: 'Balance', type: 'currency' },
    { key: 'montoAPagar', label: 'Monto a Pagar', type: 'currency' },
    { key: 'tipo', label: 'Tipo' },
    { key: 'actions', label: 'Acción' },
  ];

  acuerdosColumns = [
    { key: 'invoiceNumber', label: 'No. Factura' },
    { key: 'policyNumber', label: 'Póliza', type: 'poliza' },
    { key: 'paymentValue', label: 'Monto a Pagar', type: 'currency' },
    { key: 'actions', label: 'Acción' },
  ];

  // Propiedades para facturas seleccionadas
  visibleFacturasColumns: any[] = [];
  hiddenFacturasColumns: any[] = [];
  showFacturasColumnMenu = false;

  // Propiedades para acuerdos seleccionados
  visibleAcuerdosColumns: any[] = [];
  hiddenAcuerdosColumns: any[] = [];
  showAcuerdosColumnMenu = false;

  acuerdosAgrupados: AcuerdoAgrupado[] = [];

  tiposDocumento: { [key: number]: string } = {
    1: 'Factura',
    2: 'Acuerdo de Pago',
  };

  // Constructor
  constructor(
    private paymentService: PaymentService,
    private authService: AuthService,
    private gAnalytics: GoogleAnalyticsService
  ) {
    this.title = 'Pagos Online';
  }

  // Inicialización
  ngOnInit() {
    this.loadClientBalances();
    this.updatePagination();
    this.adjustVisibleColumns();
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize.bind(this));

    // Inicializar columnas visibles para acuerdos
    this.visibleAcuerdosColumns = [...this.acuerdosColumns];
  }

  ngOnDestroy() {
    window.removeEventListener('resize', this.checkScreenSize.bind(this));
  }

  // Cargar balances de clientes
  loadClientBalances() {
    const producerNumber = this.authService.getProducerNumber();

    if (!producerNumber) {
      this.errorMessage = 'No se pudo obtener el número de productor';
      return;
    }

    this.paymentService.getClientBalances({ producerNumber }).subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          this.insuranceData = response.data;
          this.updatePagination();
          this.clientName = response.data[0].clientName;
        }
      },
      error: (error) => {
        this.errorMessage = 'Error al cargar los balances de clientes';
        console.error('Error:', error);
      },
    });
  }

  // Seleccionar cliente
  onClientSelect(clientCode: number) {
    this.selectedClientCode = clientCode;

    const selectedClient = this.insuranceData.find(
      (client) => client.clientCode === clientCode
    );
    if (selectedClient) {
      this.clientName = selectedClient.clientName;
    }
    this.loadClientInvoices(clientCode);
    this.loadClientAgreements(clientCode);
  }

  // Cargar facturas del cliente
  loadClientInvoices(clientcode: number) {
    this.paymentService.getClientInvoices({ clientcode }).subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          this.facturasData = response.data;
          this.facturasMapeadas = response.data.invoices.map((invoice) => ({
            poliza: invoice.policyNumber,
            factura: invoice.documentNumber.toString(),
            antiguedad: invoice.antiquity.toString(),
            balance: invoice.balance,
            currencyBalance: invoice.currencyBalance,
            currencyDescription: invoice.currencyDescription,
            minimoRD: invoice.minAmount,
            pagarRD: invoice.balance,
            acuerdo: invoice.hasDeal ? 'Sí' : 'N/A',
            montoAPagar: 0,
            documentType: invoice.documentType,
          }));
        }
      },
      error: (error) => {
        this.errorMessage = 'Error al cargar las facturas del cliente';
        console.error('Error:', error);
      },
    });
  }

  // Cargar acuerdos del cliente
  loadClientAgreements(clientCode: number) {
    this.paymentService
      .getClientAgreements({ clientcode: clientCode })
      .subscribe({
        next: (response) => {
          if (response.statusCode === 200 && response.data?.agreements) {
            this.acuerdosData = response.data.agreements.map((acuerdo) => ({
              idAgreement: acuerdo.agreementNumber,
              pending: acuerdo.pending,
              installment: acuerdo.installment,
              value: acuerdo.value,
              expiration: new Date(acuerdo.expirationDate),
              paid: acuerdo.paid,
              payable: acuerdo.paymentAvailable ? 1 : 0,
              montoAPagar: undefined,
              minimumAmmount: acuerdo.minimumAmmount,
              selected: false,
              agreementInformation: acuerdo.agreementInformation.map(
                (info) => ({
                  ...info,
                  policyNumber: info.policyNumber,
                  documentNumber: info.documentNumber,
                  invoiceValue: info.invoiceValue,
                })
              ),
              paymentAvailable: acuerdo.paymentAvailable,
            }));
          } else {
            this.acuerdosData = [];
          }
        },
        error: (error) => {
          this.errorMessage = 'Error al cargar los acuerdos del cliente';
          console.error('Error:', error);
        },
      });
  }

  // Filtrar clientes
  filterClients(): PaymentBalanceResponse[] {
    if (!this.filterText.trim()) return this.insuranceData;

    const searchText = this.filterText.toLowerCase();
    return this.insuranceData.filter(
      (client) =>
        client.clientCode.toString().includes(searchText) ||
        client.clientName.toLowerCase().includes(searchText)
    );
  }

  // Mostrar modal de facturas
  showFacturas(client: ClientInvoices) {
    this.selectedClientCode = client.clientCode;


    //Evento para trackear los clicks en el boton de "Ver Facturas" en Google Analytics
    this.gAnalytics.trackEvent('ver_factura', {
      page: '/pagos-online',
      action: 'Ver factura'
    })
    const selectedClient = this.insuranceData.find(
      (c) => c.clientCode === client.clientCode
    );
    if (selectedClient) {
      this.clientName = selectedClient.clientName;
    }
    this.loadClientInvoices(client.clientCode);
    this.showFacturasModal = true;
  }

  // Mostrar modal de acuerdos
  showAcuerdos(client: PaymentBalanceResponse) {

    this.gAnalytics.trackEvent('ver_acuerdo_pago', {
      page: '/pagos-online',
      action: 'Ver acuerdo'
    })

    this.selectedClientCode = client.clientCode;
    this.clientName = client.clientName;
    this.loadClientAgreements(client.clientCode);
    this.showAcuerdosModal = true;
  }

  // Cerrar modal de facturas
  closeFacturasModal() {
    this.showFacturasModal = false;
    this.facturasData = null;
    this.selectedClientCode = null;
    this.totalAmount = 0;
    this.clientName = '';
  }

  // Cerrar modal de acuerdos
  closeAcuerdosModal() {
    this.showAcuerdosModal = false;
    this.acuerdosData = [];
    this.selectedClientCode = null;
    this.clientName = '';
  }

  // Actualizar total
  updateTotal() {
    if (!this.facturasData?.invoices) return;

    this.totalAmount = this.facturasData.invoices
      .filter((factura) => factura.selected)
      .reduce((total, factura) => total + (factura.paymentAmount || 0), 0);
  }

  canProcesarPago(): boolean {
    if (!this.facturasData?.invoices) return false;

    const hasSelectedInvoices = this.facturasData.invoices.some(
      (factura) => factura.selected
    );
    const allPaymentsValid = this.facturasData.invoices
      .filter((factura) => factura.selected)
      .every(
        (factura) =>
          factura.paymentAmount &&
          factura.paymentAmount >= (factura.minAmount || 0) &&
          factura.paymentAmount <= (factura.balance || 0)
      );

    return hasSelectedInvoices && allPaymentsValid;
  }

  // Procesar pago
  procesarPago() {

    this.gAnalytics.trackEvent('procesar_pago', {
      page: '/pagos-online',
      action: 'Accion para abrir el modal de confirmacion de pago'
    })
    const details: PaymentDetail[] = [
      // Procesar facturas individuales
      ...this.facturasSeleccionadas.map(
        (factura): PaymentDetail => ({
          PolicyNumber: factura.poliza,
          DocumentNumber: parseInt(factura.factura),
          DocumentType: factura.documentType,
          PaymentValue: factura.montoAPagar,
          tipo: 'Factura',
        })
      ),
      // Procesar facturas de acuerdos
      ...this.acuerdosAgrupados.flatMap((grupo) =>
        grupo.facturas.map(
          (factura): PaymentDetail => ({
            PolicyNumber: factura.policyNumber,
            DocumentNumber: factura.invoiceNumber,
            DocumentType: factura.documentType,
            PaymentValue: factura.paymentValue,
            tipo: 'Acuerdo',
          })
        )
      ),
    ];

    this.datosPago.Details = details;
    this.datosPago.ProducerNumber = this.authService.getProducerNumber() || 0;
    this.showConfirmacionPago = true;
  }

  // Confirmar pago
  confirmarPago() {
    this.isProcessingPayment = true;
    this.paymentService.makePayment(this.datosPago).subscribe({
      next: (response) => {
        if (response.data.cardnetPaymentIsSucessfull) {
          this.approvalNumber = response.data.cardnetApprovalCode;
          this.mostrarNotificacion(
            'success',
            'Pago exitoso',
            'El pago se ha procesado correctamente, Número de aprobación: ' +
            this.approvalNumber
          );
          this.gAnalytics.trackEvent('purchase', {
            transaction_id: response.data.cardnetApprovalCode,
            page: '/pagos-online',
            value:this.calcularTotalPago(),
            currency:'DOP',
            action: 'Confirmar pago',
            items: this.mapPaymentProducts(this.datosPago.Details)
          })
          this.cerrarModalPago();
          this.limpiarSeleccion();
          this.loadClientBalances();
        } else {
          this.mostrarNotificacion(
            'error',
            'Error en el pago',
            'No se pudo procesar el pago. Por favor, intente nuevamente'
          );
        }

        this.showConfirmacionPago = false;
        this.isProcessingPayment = false;
      },
      error: (error) => {
        this.mostrarNotificacion(
          'error',
          'Error en el pago',
          'Ocurrió un error al procesar el pago'
        );

        this.showConfirmacionPago = false;
        this.isProcessingPayment = false;
      },
    });
  }

  // Cancelar confirmación de pago
  cancelarConfirmacion() {
    this.isProcessingPayment = false;
    this.showConfirmacionPago = false;
  }

  // Obtener tipo de documento
  obtenerTipoDocumento(documentType: number): string {
    return documentType === 1 ? 'Factura' : 'Acuerdo de Pago';
  }

  // Calcular total de pago
  calcularTotalPago(): number {
    return this.datosPago.Details.reduce(
      (total, detalle) => total + detalle.PaymentValue,
      0
    );
  }

  // Seleccionar factura
  onInvoiceSelection(factura: Invoice) {
    if (!factura.selected) {
      factura.paymentAmount = 0;
    } else {
      factura.paymentAmount = factura.minAmount;
    }
    this.updateTotal();
  }

  // Actualizar paginación
  updatePagination() {
    const filteredData = this.filterClients();
    this.totalPages = Math.ceil(filteredData.length / this.itemsPerPage);
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedData = filteredData.slice(startIndex, endIndex);
  }

  // Cambiar página
  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
      this.gAnalytics.trackEvent('click_paginacion',{
      page:'/pagos-online',
      action:'Paginacion en tabla de pagos online'
    })
    }
  }

  // Obtener páginas visibles
  getVisiblePages(): number[] {
    const delta = 2;
    const range: number[] = [];
    const rangeWithDots: number[] = [];
    let l: number;

    for (
      let i = Math.max(2, this.currentPage - delta);
      i <= Math.min(this.totalPages - 1, this.currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (range[0] > 2) {
      range.unshift(-1);
    }
    if (range[range.length - 1] < this.totalPages - 1) {
      range.push(-1);
    }

    return range.filter((n) => n !== -1);
  }

  // Filtrar
  onFilterTextChange() {
    this.currentPage = 1; // Regresa a la primera página cuando se filtra
    this.updatePagination();
  }

  // Agregar facturas
  onFacturasAgregadas(facturas: any[]) {
    // Agregar solo las facturas que no estén ya en la lista
    const facturasNuevas = facturas.filter(
      (facturaNueva) =>
        !this.facturasSeleccionadas.some(
          (facturaExistente) =>
            facturaExistente.factura === facturaNueva.factura
        )
    );
    this.facturasSeleccionadas = [
      ...this.facturasSeleccionadas,
      ...facturasNuevas,
    ];
    this.showFacturasModal = false;
    this.calcularTotal();
  }

  calcularTotal() {
    const totalFacturas = this.facturasSeleccionadas.reduce(
      (sum, factura) => sum + (factura.montoAPagar || 0),
      0
    );

    const totalAcuerdos = this.acuerdosAgrupados.reduce(
      (sum, grupo) => sum + grupo.totalPagar,
      0
    );

    this.totalAPagar = totalFacturas + totalAcuerdos;
  }

  // Ordenar
  ordenar(columna: string) {
    if (this.sortColumn === columna) {
      this.sortDesc = !this.sortDesc;
    } else {
      this.sortColumn = columna;
      this.sortDesc = false;
    }

    this.insuranceData.sort((a, b) => {
      let valorA = a[columna as keyof PaymentBalanceResponse];
      let valorB = b[columna as keyof PaymentBalanceResponse];

      if (valorA === null || valorA === undefined)
        return this.sortDesc ? -1 : 1;
      if (valorB === null || valorB === undefined)
        return this.sortDesc ? 1 : -1;

      if (typeof valorA === 'number' && typeof valorB === 'number') {
        return this.sortDesc ? valorB - valorA : valorA - valorB;
      }

      const strA = String(valorA).toLowerCase();
      const strB = String(valorB).toLowerCase();

      return this.sortDesc
        ? strB.localeCompare(strA)
        : strA.localeCompare(strB);
    });

    this.updatePagination();
  }

  // Agregar acuerdos
  onAcuerdosAgregados(acuerdos: Acuerdo[]) {
    const quotesRequest: AgreementQuotesRequest = {
      quotes: acuerdos.map((acuerdo) => ({
        agreementNumber: acuerdo.idAgreement,
        quotaNumber: acuerdo.installment,
      })),
    };
    this.paymentService.getAgreementQuotes(quotesRequest).subscribe({
      next: (response) => {
        if (response.statusCode === 200 && response.data) {
          // Crear Map con acuerdos existentes
          const gruposExistentes = new Map(
            this.acuerdosAgrupados.map((grupo) => [grupo.idAgreement, grupo])
          );

          // Procesar nuevos acuerdos
          acuerdos.forEach((acuerdo) => {
            if (!gruposExistentes.has(acuerdo.idAgreement)) {
              const facturas = response.data.filter((factura) =>
                acuerdo.agreementInformation?.some(
                  (info) => info.policyNumber === factura.policyNumber
                )
              );

              if (facturas.length > 0) {
                gruposExistentes.set(acuerdo.idAgreement, {
                  idAgreement: acuerdo.idAgreement,
                  facturas: facturas,
                  totalPagar: facturas.reduce(
                    (sum, f) => sum + f.paymentValue,
                    0
                  ),
                  tipo: 'Acuerdo',
                });
              }
            }
          });

          this.acuerdosAgrupados = Array.from(gruposExistentes.values());
          this.showAcuerdosModal = false;
          this.calcularTotal();
        }
      },
      error: (error) => {
        console.error('Error al obtener cuotas de acuerdo:', error);
        this.mostrarNotificacion(
          'error',
          'Error',
          'No se pudieron obtener las cuotas del acuerdo'
        );
      },
    });
  }

  // Limpiar selección
  limpiarSeleccion() {
    this.gAnalytics.trackEvent('limpiar_seleccion', {
      page: '/pagos-online',
      action: 'Limpiar seleccion de pagos',
    })
    this.facturasSeleccionadas = [];
    this.acuerdosAgrupados = [];
    this.totalAPagar = 0;
  }

  // Abrir modal de pago
  abrirModalPago() {
    this.gAnalytics.trackEvent('abrir_modal_pago', {
      page: '/pagos-online',
      action: 'Abrir modal de pago',
    })
    this.showModalPago = true;
  }

  // Cerrar modal de pago
  cerrarModalPago() {
    this.showModalPago = false;
    this.datosPago = {
      CardNumber: '',
      Expiration: '',
      CardCvv: '',
      BankCode: null,
      ProducerNumber: 0,
      Details: [],
    };
  }

  // Eliminar factura
  eliminarFactura(factura: any) {

    this.gAnalytics.trackEvent('boton_eliminar_factura', {
      page: '/pagos-online',
      action: 'Eliminar factura seleccionada',
    })

    this.itemToDelete = { tipo: 'factura', data: factura };
    this.showConfirmModal = true;
  }

  // Eliminar acuerdo
  eliminarAcuerdo(grupo: AcuerdoAgrupado) {
    this.itemToDelete = {
      tipo: 'acuerdo',
      data: {
        idAgreement: grupo.idAgreement,
        montoAPagar: grupo.totalPagar,
      },
    };
    this.showConfirmModal = true;
  }

  // Confirmar eliminación
  confirmarEliminacion() {
    if (this.itemToDelete) {
      if (this.itemToDelete.tipo === 'factura') {
        this.gAnalytics.trackEvent('confirmar_eliminacion_factura', {
          page: '/pagos-online',
          action: 'Confirmar Eliminicacion de Factura Seleccionada',
        })
        this.facturasSeleccionadas = this.facturasSeleccionadas.filter(
          (f) => f.factura !== this.itemToDelete?.data.factura
        );
      } else {
        this.acuerdosAgrupados = this.acuerdosAgrupados.filter(
          (a) => a.idAgreement !== this.itemToDelete?.data.idAgreement
        );
      }
      this.calcularTotal();
    }
    this.cancelarEliminacion();
  }

  // Cancelar eliminación
  cancelarEliminacion() {
    this.gAnalytics.trackEvent('cancelar_eliminacion_factura', {
      page: '/pagos-online',
      action: 'Cancelar eliminicacion',
    })
    this.showConfirmModal = false;
    this.itemToDelete = null;
  }

  // Validación de CVV
  validarCVV(event: any) {
    // Prevenir entrada de no números
    if (!/^\d*$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Limitar longitud
    if (event.target.value.length >= 5 && event.key !== 'Backspace') {
      event.preventDefault();
    }
  }

  formatearNumeroTarjeta(event: any) {
    const input = event.target;

    // Remover todo excepto números
    let value = input.value.replace(/\D/g, '');

    // Limitar a 16 dígitos
    value = value.substring(0, 16);

    // Formatear con guiones
    const formattedValue = value.replace(/(\d{4})/g, '$1-').replace(/-$/, '');

    // Actualizar el input y el modelo
    input.value = formattedValue;
    this.datosPago.CardNumber = value;

    // Mover el cursor al final
    const cursorPos = input.value.length;
    input.setSelectionRange(cursorPos, cursorPos);
  }

  validarFecha(event: any) {
    const input = event.target;

    // Prevenir entrada de no números
    if (!/^\d*$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    // Manejar el formato MM/YY
    if (input.value.length === 2 && event.key !== 'Backspace') {
      input.value += '/';
    }

    // Validar mes (01-12)
    if (input.value.length === 1 && parseInt(input.value + event.key) > 12) {
      event.preventDefault();
    }

    // Limitar longitud total
    if (input.value.length >= 5 && event.key !== 'Backspace') {
      event.preventDefault();
    }
  }

  validarFormularioPago(): boolean {
    const cardNumber = this.datosPago.CardNumber;
    const expiration = this.datosPago.Expiration;
    const cvv = this.datosPago.CardCvv;

    return (
      cardNumber.length === 16 &&
      /^(0[1-9]|1[0-2])\/\d{2}$/.test(expiration) &&
      (cvv.length === 3 || cvv.length === 4 || cvv.length === 5)
    );
  }

  // Mostrar notificación
  mostrarNotificacion(
    type: 'success' | 'error',
    title: string,
    message: string
  ) {
    this.notificationType = type;
    this.notificationTitle = title;
    this.notificationMessage = message;
    this.showNotification = true;

    setTimeout(() => {
      this.showNotification = false;
    }, 15000);
  }

  // Formatear poliza
  formatearPoliza(poliza: string): string {
    const str = poliza.toString();
    if (str.length >= 10) {
      return `${str.slice(0, 1)}-${str.slice(1, 2)}-${str.slice(
        2,
        5
      )}-${str.slice(5)}`;
    }
    return str;
  }

  // Ajustar columnas visibles
  adjustVisibleColumns() {
    const width = window.innerWidth;
    if (width < 600) {
      this.visibleColumns = this.clientesColumns.slice(0, 2);
    } else if (width < 900) {
      this.visibleColumns = this.clientesColumns.slice(0, 3);
    } else {
      this.visibleColumns = [...this.clientesColumns];
    }
    this.hiddenColumns = this.clientesColumns.slice(this.visibleColumns.length);
  }

  toggleColumnMenu() {
    this.showColumnMenu = !this.showColumnMenu;
  }

  switchColumn(column: any) {
    this.visibleColumns[this.visibleColumns.length - 1] = column;
    this.hiddenColumns = this.clientesColumns.filter(
      (col) => !this.visibleColumns.includes(col)
    );
    this.showColumnMenu = false;
  }

  trackByFn(index: number, item: any): string {
    if (item.agreementNumber && item.quotaNumber !== undefined) {
      // Para acuerdos, combinar número de acuerdo y número de cuota
      return `acuerdo_${item.agreementNumber}_${item.quotaNumber}`;
    } else if (item.factura) {
      // Para facturas
      return `factura_${item.factura}`;
    } else if (item.clientCode) {
      // Para clientes
      return `cliente_${item.clientCode}`;
    }
    // Fallback a índice si no hay otros identificadores
    return `index_${index}`;
  }

  formatValue(value: any, column: any): string {
    if (value === undefined || value === null) return '';

    switch (column.type) {
      case 'currency':
        return new Intl.NumberFormat('es-DO', {
          style: 'currency',
          currency: 'DOP',
        }).format(value);
      case 'date':
        return new Date(value).toLocaleDateString();
      case 'poliza':
        return this.formatearPoliza(value);
      default:
        return value.toString();
    }
  }

  private checkScreenSize() {
    const width = window.innerWidth;
    this.isSmallScreen = width < 600;

    if (this.isSmallScreen) {
      // Ajustar columnas de facturas y acuerdos
      this.visibleFacturasColumns = this.facturasColumns.slice(0, 2);
      this.hiddenFacturasColumns = this.facturasColumns.slice(2);

      this.visibleAcuerdosColumns = this.acuerdosColumns.slice(0, 2);
      this.hiddenAcuerdosColumns = this.acuerdosColumns.slice(2);
    } else {
      this.visibleFacturasColumns = [...this.facturasColumns];
      this.hiddenFacturasColumns = [];

      this.visibleAcuerdosColumns = [...this.acuerdosColumns];
      this.hiddenAcuerdosColumns = [];
    }
  }

  toggleFacturasColumnMenu() {
    this.showFacturasColumnMenu = !this.showFacturasColumnMenu;
  }

  toggleAcuerdosColumnMenu() {
    this.showAcuerdosColumnMenu = !this.showAcuerdosColumnMenu;
  }

  switchFacturasColumn(column: any) {
    this.visibleFacturasColumns[this.visibleFacturasColumns.length - 1] =
      column;
    this.hiddenFacturasColumns = this.facturasColumns.filter(
      (col) => !this.visibleFacturasColumns.includes(col)
    );
    this.showFacturasColumnMenu = false;
  }

  switchAcuerdosColumn(column: any) {
    this.visibleAcuerdosColumns[this.visibleAcuerdosColumns.length - 1] =
      column;
    this.hiddenAcuerdosColumns = this.acuerdosColumns.filter(
      (col) => !this.visibleAcuerdosColumns.includes(col)
    );
    this.showAcuerdosColumnMenu = false;
  }

  onPaste(event: any) {
    setTimeout(() => this.formatearNumeroTarjeta(event), 0);
  }

  mapPaymentProducts(selected: any) {
    const product: { item_id: number, item_name: string, quantity: number, price: number }[] = []

    selected.forEach((s: any) => {
      console.log(s)
      product.push({
        item_id: s.DocumentNumber,
        item_name: s.tipo,
        quantity: 1,
        price: s.PaymentValue
      })
    })
    return product;
  }
}
